import * as moment from "moment";

/**
 * LTA status is derived from the agreement term rather than stored, so it can
 * never drift out of date the way a saved value would. Mirrors
 * `cims-backend/src/api/lta/ltaStatus.ts` — keep the two in step.
 */
export const LTA_STATUSES = [
  { value: "ACTIVE", label: "Active", color: "success" },
  { value: "UPCOMING", label: "Upcoming", color: "info" },
  { value: "EXPIRED", label: "Expired", color: "danger" },
] as const;

export type LTAStatus = (typeof LTA_STATUSES)[number]["value"];

const today = () => moment().format("YYYY-MM-DD");
const asDay = (value?: string | null) =>
  value ? moment(value).format("YYYY-MM-DD") : null;

/**
 * Returns null when the end date is missing — without it there is no basis for
 * calling an agreement active or lapsed.
 */
export function deriveLtaStatus(
  startDate?: string | null,
  endDate?: string | null
): LTAStatus | null {
  const end = asDay(endDate);
  if (!end) return null;
  const start = asDay(startDate);
  const now = today();
  if (end < now) return "EXPIRED";
  if (start && start > now) return "UPCOMING";
  return "ACTIVE";
}

export const getStatusMeta = (status?: string | null) =>
  LTA_STATUSES.find((s) => s.value === status);

/**
 * Translates a status choice into filters over the term dates, so the rows the
 * table returns always match the badges it renders.
 *
 * Deliberately mirrors `deriveLtaStatus`: an agreement is Expired once its end
 * date has passed, Upcoming while its start date is still ahead, and Active in
 * between. A missing start date counts as already started.
 */
export function statusFilterConditions(status?: string | null): any[] {
  const now = today();
  switch (status) {
    case "ACTIVE":
      return [
        { endDate: { $gte: now } },
        {
          $or: [{ startDate: { $null: true } }, { startDate: { $lte: now } }],
        },
      ];
    case "UPCOMING":
      return [{ startDate: { $gt: now } }, { endDate: { $gte: now } }];
    case "EXPIRED":
      return [{ endDate: { $lt: now } }];
    default:
      return [];
  }
}

/** An agreement counts as expiring once it is inside this window of its end date. */
export const EXPIRING_SOON_DAYS = 90;

/**
 * Days remaining on the agreement. Negative once the end date has passed,
 * `null` when no end date has been recorded.
 */
export function daysRemaining(endDate?: string | null): number | null {
  if (!endDate) return null;
  return moment(endDate).startOf("day").diff(moment().startOf("day"), "days");
}

export function isExpiringSoon(endDate?: string | null): boolean {
  const days = daysRemaining(endDate);
  return days !== null && days >= 0 && days <= EXPIRING_SOON_DAYS;
}

/**
 * How far through its term the agreement is, as a 0-100 percentage. Used for
 * the term progress bars; returns `null` when either date is missing.
 */
export function termProgress(
  startDate?: string | null,
  endDate?: string | null
): number | null {
  if (!startDate || !endDate) return null;
  const start = moment(startDate);
  const end = moment(endDate);
  const total = end.diff(start, "days");
  if (total <= 0) return null;
  const elapsed = moment().diff(start, "days");
  return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
}
